import React from 'react';
import _ from 'lodash';
import { makeStyles, Paper, Tooltip, Typography } from '@material-ui/core';
import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  ViewDayOutlined,
} from '@material-ui/icons';

import { Datatable } from '../../../components';
import { operationalTierMap } from './Constants';
import { CircuitBreakerTooltip } from './CircuitBreakerTooltip';
import { OperationalTierTooltip } from './OperationalTierTooltip';
import { useHistory } from 'react-router';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    width: '100%',
    padding: '20px',
    margin: '0',
  },
  pointer: {
    cursor: 'pointer',
  },
  pingDot: {
    position: 'relative',
    display: 'inline-block',
    width: 12,
    height: 12,
    borderRadius: '50%',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: '$ping 2s cubic-bezier(0, 0, 0.2, 1) infinite',
    },
  },
  green: {
    backgroundColor: '#10b981',
    '&::before': {
      backgroundColor: '#10b981',
    },
  },
  red: {
    backgroundColor: '#ef4444',
    '&::before': {
      backgroundColor: '#ef4444',
    },
  },
  yellow: {
    backgroundColor: '#fbbf24',
    '&::before': {
      backgroundColor: '#fbbf24',
    },
  },
  '@keyframes ping': {
    '0%': {
      transform: 'scale(1)',
      opacity: 1,
    },
    '75%, 100%': {
      transform: 'scale(2)',
      opacity: 0,
    },
  },
  tooltipLegendContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    width: '20rem',
  },
  featuresTooltip: {
    maxWidth: 'none',
  },
}));

export const AndromedaAgentConfigurationTable = ({
  data,
  loading,
  openEditModal,
}) => {
  const classes = useStyles();
  const history = useHistory();

  const tableConfig = [
    {
      sortable: true,
      heading: 'Agent Name',
      key: 'name',
      formatter: (val) => (
        <Typography color="textPrimary" variant="body2">
          {val}
        </Typography>
      ),
    },
    {
      sortable: true,
      heading: 'Enabled',
      key: 'enabled',
      formatter: (val) =>
        !!val ? (
          <CheckOutlined color="secondary" />
        ) : (
          <CloseOutlined color="error" />
        ),
    },
    {
      sortable: false,
      heading: 'Circuit Breaker',
      key: 'circuitBreaker',
      formatter: (val) => {
        const statesToColors = {
          open: classes.red,
          halfOpen: classes.yellow,
          closed: classes.green,
        };

        return (
          <Tooltip title={<CircuitBreakerTooltip circuitBreaker={val} />}>
            <span
              className={`${classes.pingDot} ${statesToColors[val.state]} ${
                classes.pointer
              }`}
            />
          </Tooltip>
        );
      },
    },
    {
      sortable: false,
      heading: 'Tier',
      key: 'operationalTier',
      formatter: (val) => {
        const tier = operationalTierMap[val];
        return (
          <Tooltip
            classes={{ tooltip: classes.featuresTooltip }}
            title={<OperationalTierTooltip tier={tier} />}
          >
            <Typography className={classes.pointer} variant="body2">
              {tier.name}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      sortable: false,
      heading: 'Has Captcha',
      key: 'hasCaptcha',
      formatter: (val) =>
        !!val ? (
          <CheckOutlined color="secondary" />
        ) : (
          <CloseOutlined color="error" />
        ),
    },
    {
      sortable: false,
      heading: 'Allows Multiple Numbers',
      key: 'allowsMultipleNumbers',
      formatter: (val) =>
        !!val ? (
          <CheckOutlined color="secondary" />
        ) : (
          <CloseOutlined color="error" />
        ),
    },
    {
      sortable: false,
      heading: 'View Jobs',
      key: 'name',
      formatter: (val) => (
        <ViewDayOutlined
          className={classes.pointer}
          color="primary"
          onClick={() =>
            history.push(`/dashboard/andromeda/jobs-explorer?agent=${val}`)
          }
        />
      ),
    },
    {
      sortable: false,
      heading: 'Edit Agent',
      key: 'name',
      formatter: (val) => (
        <EditOutlined
          className={classes.pointer}
          color="primary"
          onClick={() => {
            openEditModal(_.cloneDeep(data.find((x) => x.name === val)));
          }}
        />
      ),
    },
  ];

  return (
    <section className={classes.wrapper}>
      <Paper>
        <Datatable
          tableHeading="Andromeda Agent Configurations"
          loading={loading}
          tableConfig={tableConfig}
          defaultSortColumn={'name'}
          data={data}
          paginationLeft={false}
        />
      </Paper>
    </section>
  );
};
